import {
  Box,
  Checkbox,
  Divider,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import RightDrawer from "components/base/RightDrawer";

export default function GroupPreferenceDrawer({
  feature,
  gradeColumns,
  disabled,
  useCheckboxes = false,
  onClose,
  onChange,
  onChangeAll,
}) {
  const theme = useTheme();
  const PreferenceControl = useCheckboxes ? Checkbox : Switch;
  const headerBackground = theme.palette.secondary.main;
  const allChecked = gradeColumns.every(({ value }) =>
    Boolean(feature?.[value]),
  );
  const someChecked = gradeColumns.some(({ value }) =>
    Boolean(feature?.[value]),
  );

  return (
    <RightDrawer open={Boolean(feature)} onClose={onClose}>
      {feature && (
        <Box
          role="dialog"
          aria-modal="true"
          aria-labelledby="group-preference-title"
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={2}
            sx={{
              p: 3,
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: headerBackground,
            }}
          >
            <Typography
              id="group-preference-title"
              variant="h5"
              sx={{
                color: theme.palette.getContrastText(headerBackground),
                overflowWrap: "anywhere",
                minWidth: 0,
              }}
            >
              {feature.feature}
            </Typography>
            <AccessibleIconButton
              title="Close"
              onClick={onClose}
              sx={{ flexShrink: 0 }}
            >
              <CloseIcon sx={{ height: 18, width: 18 }} />
            </AccessibleIconButton>
          </Stack>
          <Stack spacing={3} sx={{ p: 3 }}>
            <Typography variant="body1">{feature.description}</Typography>
            <Divider />
            <Box>
              <Typography variant="h6" color="primary.dark" sx={{ mb: 2 }}>
                Grades
              </Typography>
              <Stack divider={<Divider />}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                  py={1}
                >
                  <Box>
                    <Typography variant="body1">All</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {allChecked
                        ? feature.restrictedLabel
                        : someChecked
                          ? "Mixed"
                          : feature.unrestrictedLabel}
                    </Typography>
                  </Box>
                  <PreferenceControl
                    size="small"
                    checked={allChecked}
                    {...(useCheckboxes && { indeterminate: someChecked && !allChecked })}
                    disabled={disabled}
                    inputProps={{ "aria-label": `${feature.feature} for all` }}
                    onChange={(_, checked) => onChangeAll(feature.id, checked)}
                  />
                </Stack>
                {gradeColumns.map(({ value, label }) => (
                  <Stack
                    key={value}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                    py={1}
                  >
                    <Box>
                      <Typography variant="body1">{label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature[value]
                          ? feature.restrictedLabel
                          : feature.unrestrictedLabel}
                      </Typography>
                    </Box>
                    <PreferenceControl
                      size="small"
                      checked={Boolean(feature[value])}
                      disabled={disabled}
                      inputProps={{
                        "aria-label": `${feature.feature} for ${label}`,
                      }}
                      onChange={(_, checked) =>
                        onChange(feature.id, { [value]: checked })
                      }
                    />
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}
    </RightDrawer>
  );
}
